import { useEffect, useState, useCallback } from "react";
import { Save, CheckCircle2, Video, Trash2, ClipboardPaste, ChevronDown, ChevronUp, LifeBuoy, Upload, Copy } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const inputCls = "bg-navy border border-white/15 text-white px-3 py-2 text-sm focus:border-safety outline-none";

function AssetLibraryPanel() {
  const [open, setOpen] = useState(false);
  const [assets, setAssets] = useState([]);
  const [file, setFile] = useState(null);
  const [visibility, setVisibility] = useState("members");
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/video-assets");
      setAssets(data.assets);
    } catch {
      toast.error("Failed to load video assets");
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const CHUNK_SIZE = 8 * 1024 * 1024;

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    setProgress(0);
    try {
      const { data: init } = await api.post("/admin/video-assets/init", {
        filename: file.name,
        content_type: file.type || "video/mp4",
        total_size: file.size,
        visibility,
      });
      const total = Math.ceil(file.size / CHUNK_SIZE);
      for (let i = 0; i < total; i++) {
        const chunk = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        await api.post(`/admin/video-assets/${init.upload_id}/chunk?index=${i}`, chunk, {
          headers: { "Content-Type": "application/octet-stream" },
        });
        setProgress(Math.round(((i + 1) * 100) / total));
      }
      await api.post(`/admin/video-assets/${init.upload_id}/complete`);
      toast.success("Video uploaded to the media library");
      setFile(null);
      setProgress(0);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const copy = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Media URL copied — paste it into any video field");
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/video-assets/${id}`);
      toast.success("Asset removed");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const fmtSize = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

  return (
    <div className="bg-charcoal border border-white/10">
      <button onClick={() => setOpen(!open)} data-testid="asset-library-toggle"
        className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-white/5 transition-colors">
        <span className="flex items-center gap-3 font-display font-bold text-white">
          <Upload size={17} className="text-safety" /> Video asset library — upload MP4 files
        </span>
        {open ? <ChevronUp size={17} className="text-slate-400" /> : <ChevronDown size={17} className="text-slate-400" />}
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-white/10 pt-5" data-testid="asset-library-panel">
          <p className="text-slate-400 text-xs mb-4 leading-relaxed">
            Upload MP4/WEBM/MOV files to hosted storage, then copy each media URL into a lesson, help-video, or section field below.
            <span className="text-slate-300"> Members only</span> = signed-in members can watch.{" "}
            <span className="text-slate-300">Public</span> = anyone can watch (use only for the homepage explainer).
          </p>
          <div className="flex flex-wrap items-center gap-2.5 mb-4">
            <label className="flex items-center gap-2 bg-navy border border-dashed border-white/20 hover:border-safety/50 px-4 py-2.5 text-sm text-slate-400 cursor-pointer transition-colors"
              data-testid="asset-file-drop">
              <Upload size={14} className="text-safety shrink-0" />
              {file ? <span className="text-white">{file.name}</span> : "Choose video file…"}
              <input type="file" accept="video/mp4,video/webm,video/quicktime" data-testid="asset-file-input" className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
            <select value={visibility} onChange={(e) => setVisibility(e.target.value)} data-testid="asset-visibility-select" className={inputCls}>
              <option value="members">Members only</option>
              <option value="public">Public (website)</option>
            </select>
            <button onClick={upload} disabled={!file || uploading} data-testid="asset-upload-btn"
              className="bg-safety hover:bg-safety-hover disabled:opacity-40 text-white font-semibold px-5 py-2.5 text-sm transition-colors">
              {uploading ? `Uploading ${progress}%` : "Upload"}
            </button>
          </div>
          {uploading && (
            <div className="h-1.5 bg-navy border border-white/10 mb-4" data-testid="asset-progress">
              <div className="h-full bg-safety transition-all" style={{ width: `${progress}%` }} />
            </div>
          )}
          {assets.length === 0 ? (
            <p className="text-slate-500 text-sm">No uploaded videos yet.</p>
          ) : (
            <div className="space-y-px border border-white/10" data-testid="asset-list">
              {assets.map((a) => (
                <div key={a.asset_id} className="bg-navy px-4 py-3 flex flex-wrap items-center gap-3" data-testid={`asset-row-${a.asset_id.slice(0, 8)}`}>
                  <div className="flex-1 min-w-[220px]">
                    <p className="text-sm text-slate-200 font-medium">{a.original_filename}</p>
                    <p className="font-mono text-[11px] text-slate-500 mt-0.5">{a.media_url} · {fmtSize(a.size)}</p>
                  </div>
                  <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border ${
                    a.visibility === "public" ? "text-sky-300 border-sky-400/40 bg-sky-400/10" : "text-emerald-400 border-emerald-500/40 bg-emerald-500/10"
                  }`} data-testid={`asset-visibility-${a.asset_id.slice(0, 8)}`}>{a.visibility === "public" ? "Public" : "Members"}</span>
                  <button onClick={() => copy(a.media_url)} data-testid={`asset-copy-${a.asset_id.slice(0, 8)}`}
                    className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 border border-white/15 text-slate-300 hover:text-white hover:border-safety transition-colors">
                    <Copy size={12} /> Copy URL
                  </button>
                  <button onClick={() => remove(a.asset_id)} data-testid={`asset-delete-${a.asset_id.slice(0, 8)}`}
                    className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 border border-white/15 text-slate-400 hover:text-red-400 hover:border-red-500/40 transition-colors">
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SectionVideoPanel() {
  const [open, setOpen] = useState(false);
  const [sections, setSections] = useState([]);
  const [records, setRecords] = useState({});
  const [drafts, setDrafts] = useState({});
  const [savedKey, setSavedKey] = useState("");

  const load = useCallback(async () => {
    try {
      const [content, videos] = await Promise.all([api.get("/hub/content"), api.get("/admin/section-videos")]);
      setSections(content.data.sections || []);
      const rec = {}, d = {};
      videos.data.videos.forEach((v) => {
        rec[v.section_key] = v;
        d[v.section_key] = { video_url: v.video_url, status: v.status || "draft" };
      });
      setRecords(rec);
      setDrafts(d);
    } catch {
      toast.error("Failed to load section videos");
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const draftFor = (key) => drafts[key] || { video_url: "", status: "draft" };
  const setDraft = (key, patch) => setDrafts({ ...drafts, [key]: { ...draftFor(key), ...patch } });

  const save = async (key) => {
    try {
      await api.put("/admin/section-videos", { section_key: key, ...draftFor(key) });
      setSavedKey(key);
      setTimeout(() => setSavedKey(""), 2000);
      toast.success(draftFor(key).video_url ? "Section video saved" : "Section video cleared");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  return (
    <div className="bg-charcoal border border-white/10">
      <button onClick={() => setOpen(!open)} data-testid="section-video-toggle"
        className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-white/5 transition-colors">
        <span className="flex items-center gap-3 font-display font-bold text-white">
          <Video size={17} className="text-safety" /> Hub section videos — Day 1, Pipeline Engine, Partner, Team Onboarding
        </span>
        {open ? <ChevronUp size={17} className="text-slate-400" /> : <ChevronDown size={17} className="text-slate-400" />}
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-white/10 pt-5" data-testid="section-video-panel">
          <p className="text-slate-400 text-xs mb-4 leading-relaxed">
            Videos for the four member-resource sections in the Hub sidebar. New entries are{" "}
            <span className="text-amber-300">draft by default</span> — members see a placeholder until you publish. Blank URL removes the video.
          </p>
          <div className="space-y-3">
            {sections.map((s) => {
              const d = draftFor(s.key);
              const rec = records[s.key];
              return (
                <div key={s.key} className="bg-navy border border-white/10 p-3.5" data-testid={`section-video-row-${s.key}`}>
                  <div className="flex flex-wrap items-center gap-2 mb-2.5">
                    <p className="text-sm text-slate-200 font-medium">{s.title}</p>
                    {rec && (
                      <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border ${
                        rec.status === "published" ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" : "text-amber-300 border-amber-400/40 bg-amber-400/10"
                      }`} data-testid={`section-video-status-${s.key}`}>{rec.status}</span>
                    )}
                  </div>
                  <div className="grid sm:grid-cols-12 gap-2.5">
                    <input type="text" placeholder="https://… or /api/media/…" value={d.video_url}
                      onChange={(e) => setDraft(s.key, { video_url: e.target.value })}
                      data-testid={`section-video-input-${s.key}`} className={`${inputCls} sm:col-span-9`} />
                    <select value={d.status} onChange={(e) => setDraft(s.key, { status: e.target.value })}
                      data-testid={`section-video-status-select-${s.key}`} className={`${inputCls} sm:col-span-3`}>
                      <option value="draft">Draft (hidden)</option>
                      <option value="published">Published</option>
                    </select>
                  </div>
                  <button onClick={() => save(s.key)} data-testid={`section-video-save-${s.key}`}
                    className={`mt-2.5 flex items-center gap-2 text-xs font-semibold px-4 py-2 transition-colors ${
                      savedKey === s.key ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/40" : "bg-safety hover:bg-safety-hover text-white"
                    }`}>
                    {savedKey === s.key ? <CheckCircle2 size={13} /> : <Save size={13} />}
                    {savedKey === s.key ? "Saved" : "Save"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function StuckInsightsPanel() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/stuck-insights");
      setData(data);
    } catch {
      toast.error("Failed to load insights");
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  return (
    <div className="bg-charcoal border border-white/10">
      <button onClick={() => setOpen(!open)} data-testid="stuck-insights-toggle"
        className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-white/5 transition-colors">
        <span className="flex items-center gap-3 font-display font-bold text-white">
          <LifeBuoy size={17} className="text-safety" /> Stuck-point insights — what members open most
        </span>
        {open ? <ChevronUp size={17} className="text-slate-400" /> : <ChevronDown size={17} className="text-slate-400" />}
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-white/10 pt-5" data-testid="stuck-insights-panel">
          <p className="text-slate-400 text-xs mb-4 leading-relaxed">
            Counts every time a member expands an "I'm stuck" obstacle. High counts show where lessons need clearer walkthroughs or a help video first.
          </p>
          {!data || data.insights.length === 0 ? (
            <p className="text-slate-500 text-sm" data-testid="stuck-insights-empty">No member activity yet — insights appear as members open obstacles.</p>
          ) : (
            <div className="space-y-px border border-white/10" data-testid="stuck-insights-list">
              {data.insights.map((r) => (
                <div key={`${r.lesson_key}-${r.stuck_id}`} className="bg-navy px-4 py-3 flex items-center gap-3"
                  data-testid={`insight-${r.lesson_key.replace(":", "-")}-${r.stuck_id}`}>
                  <span className="font-mono text-xs font-bold text-safety border border-safety/40 bg-safety/10 px-2 py-1 shrink-0 w-12 text-center"
                    data-testid={`insight-count-${r.lesson_key.replace(":", "-")}-${r.stuck_id}`}>{r.count}×</span>
                  <div className="min-w-0">
                    <p className="text-sm text-slate-200 font-medium truncate">{r.label}</p>
                    <p className="font-mono text-[10px] text-slate-500 mt-0.5">{r.lesson_title}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function HelpVideoPanel({ modules }) {
  const [open, setOpen] = useState(false);
  const [records, setRecords] = useState({});
  const [drafts, setDrafts] = useState({});
  const [savedKey, setSavedKey] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/help-videos");
      const rec = {}, d = {};
      data.videos.forEach((v) => {
        rec[v.lesson_key] = v;
        d[v.lesson_key] = { video_url: v.video_url, title: v.title || "", status: v.status || "draft" };
      });
      setRecords(rec);
      setDrafts(d);
    } catch {
      toast.error("Failed to load help videos");
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const draftFor = (key) => drafts[key] || { video_url: "", title: "", status: "draft" };
  const setDraft = (key, patch) => setDrafts({ ...drafts, [key]: { ...draftFor(key), ...patch } });

  const save = async (key) => {
    try {
      await api.put("/admin/help-videos", { lesson_key: key, ...draftFor(key) });
      setSavedKey(key);
      setTimeout(() => setSavedKey(""), 2000);
      toast.success(draftFor(key).video_url ? "Help video saved" : "Help video cleared");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  return (
    <div className="bg-charcoal border border-white/10">
      <button onClick={() => setOpen(!open)} data-testid="help-video-toggle"
        className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-white/5 transition-colors">
        <span className="flex items-center gap-3 font-display font-bold text-white">
          <LifeBuoy size={17} className="text-safety" /> Get Unstuck help videos — per lesson
        </span>
        {open ? <ChevronUp size={17} className="text-slate-400" /> : <ChevronDown size={17} className="text-slate-400" />}
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-white/10 pt-5" data-testid="help-video-panel">
          <p className="text-slate-400 text-xs mb-4 leading-relaxed">
            Short educational help videos shown in each lesson's Get Unstuck section. New entries are{" "}
            <span className="text-amber-300">draft by default</span> — members see nothing until you publish. Blank URL removes the video.
          </p>
          {modules.map((m) => (
            <div key={m.id} className="mb-5" data-testid={`help-video-module-${m.id}`}>
              <p className="font-mono text-[11px] tracking-[0.18em] text-safety mb-2">[{m.code}] {m.title}</p>
              <div className="space-y-3">
                {m.lessons.map((l) => {
                  const key = `${m.id}:${l.id}`;
                  const d = draftFor(key);
                  const rec = records[key];
                  const tid = key.replace(":", "-");
                  return (
                    <div key={key} className="bg-navy border border-white/10 p-3.5" data-testid={`help-video-row-${tid}`}>
                      <div className="flex flex-wrap items-center gap-2 mb-2.5">
                        <p className="text-sm text-slate-200 font-medium">{l.title}</p>
                        {rec && (
                          <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border ${
                            rec.status === "published" ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" : "text-amber-300 border-amber-400/40 bg-amber-400/10"
                          }`} data-testid={`help-video-status-${tid}`}>{rec.status}</span>
                        )}
                      </div>
                      <div className="grid sm:grid-cols-12 gap-2.5">
                        <input type="url" placeholder="https://youtube.com/watch?v=…" value={d.video_url}
                          onChange={(e) => setDraft(key, { video_url: e.target.value })}
                          data-testid={`help-video-input-${tid}`} className={`${inputCls} sm:col-span-5`} />
                        <input placeholder="Title (optional)" value={d.title}
                          onChange={(e) => setDraft(key, { title: e.target.value })}
                          data-testid={`help-video-title-${tid}`} className={`${inputCls} sm:col-span-4`} />
                        <select value={d.status} onChange={(e) => setDraft(key, { status: e.target.value })}
                          data-testid={`help-video-status-select-${tid}`} className={`${inputCls} sm:col-span-3`}>
                          <option value="draft">Draft (hidden)</option>
                          <option value="published">Published</option>
                        </select>
                      </div>
                      <button onClick={() => save(key)} data-testid={`help-video-save-${tid}`}
                        className={`mt-2.5 flex items-center gap-2 text-xs font-semibold px-4 py-2 transition-colors ${
                          savedKey === key ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/40" : "bg-safety hover:bg-safety-hover text-white"
                        }`}>
                        {savedKey === key ? <CheckCircle2 size={13} /> : <Save size={13} />}
                        {savedKey === key ? "Saved" : "Save"}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminLessonVideos() {
  const [modules, setModules] = useState([]);
  const [records, setRecords] = useState({});
  const [drafts, setDrafts] = useState({});
  const [savedKey, setSavedKey] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkModule, setBulkModule] = useState("mod-01");
  const [bulkText, setBulkText] = useState("");
  const [bulkResults, setBulkResults] = useState(null);

  const load = useCallback(async () => {
    try {
      const [content, videos] = await Promise.all([api.get("/hub/content"), api.get("/admin/videos")]);
      setModules(content.data.modules);
      const rec = {};
      videos.data.videos.forEach((v) => { rec[v.lesson_key] = v; });
      setRecords(rec);
      const d = {};
      videos.data.videos.forEach((v) => {
        d[v.lesson_key] = { video_url: v.video_url, duration: v.duration || "", description: v.description || "", status: v.status || "published" };
      });
      setDrafts(d);
    } catch {
      toast.error("Failed to load lessons");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const draftFor = (key) => drafts[key] || { video_url: "", duration: "", description: "", status: "published" };
  const setDraft = (key, patch) => setDrafts({ ...drafts, [key]: { ...draftFor(key), ...patch } });

  const save = async (key) => {
    try {
      await api.put("/admin/lesson-videos", { lesson_key: key, ...draftFor(key) });
      setSavedKey(key);
      setTimeout(() => setSavedKey(""), 2000);
      toast.success(draftFor(key).video_url ? "Video saved" : "Video cleared");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (key) => {
    try {
      await api.delete(`/admin/videos/${key}`);
      toast.success("Video record deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const runBulk = async () => {
    try {
      const lines = bulkText.split("\n");
      const { data } = await api.post("/admin/videos/bulk", { module_id: bulkModule, lines });
      setBulkResults(data.results);
      const ok = data.results.filter((r) => r.status === "saved").length;
      toast.success(`${ok} video${ok === 1 ? "" : "s"} imported`);
      setBulkText("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk import failed");
    }
  };

  return (
    <div data-testid="admin-lesson-videos" className="max-w-5xl space-y-6">
      <div className="bg-charcoal border border-white/10 p-5 flex items-start gap-3">
        <Video size={18} className="text-safety shrink-0 mt-0.5" />
        <p className="text-sm text-slate-300 leading-relaxed">
          Manage a full video record per lesson: URL, duration, description, order, and published/draft status.
          Drafts stay hidden from members. No videos exist until you add real links here.
        </p>
      </div>

      <div className="bg-charcoal border border-white/10">
        <button onClick={() => setBulkOpen(!bulkOpen)} data-testid="bulk-toggle"
          className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-white/5 transition-colors">
          <span className="flex items-center gap-3 font-display font-bold text-white">
            <ClipboardPaste size={17} className="text-safety" /> Bulk import — paste many links at once
          </span>
          {bulkOpen ? <ChevronUp size={17} className="text-slate-400" /> : <ChevronDown size={17} className="text-slate-400" />}
        </button>
        {bulkOpen && (
          <div className="px-5 pb-5 border-t border-white/10 pt-5" data-testid="bulk-panel">
            <p className="text-slate-400 text-xs mb-4 leading-relaxed">
              One line per lesson, in lesson order. Format: <span className="font-mono text-slate-300">video-url | duration | description</span> (only the URL is required).
            </p>
            <div className="flex flex-wrap gap-3 mb-4">
              <select value={bulkModule} onChange={(e) => setBulkModule(e.target.value)} data-testid="bulk-module-select" className={inputCls}>
                {modules.map((m) => <option key={m.id} value={m.id}>{m.code} — {m.title}</option>)}
              </select>
            </div>
            <div className="bg-navy border border-white/10 p-3 mb-3 font-mono text-[11px] text-slate-500">
              {modules.find((m) => m.id === bulkModule)?.lessons.map((l, i) => (
                <p key={l.id}>line {i + 1} → {l.title}</p>
              ))}
            </div>
            <textarea rows={5} value={bulkText} onChange={(e) => setBulkText(e.target.value)} data-testid="bulk-textarea"
              placeholder={"https://youtube.com/watch?v=… | 12:30 | Pricing walkthrough\nhttps://vimeo.com/… | 8:05 | Template build"}
              className={`${inputCls} w-full font-mono resize-y`} />
            <button onClick={runBulk} disabled={!bulkText.trim()} data-testid="bulk-apply-btn"
              className="mt-3 bg-safety hover:bg-safety-hover disabled:opacity-40 text-white font-semibold px-6 py-3 text-sm transition-colors">
              Import lines
            </button>
            {bulkResults && (
              <div className="mt-4 space-y-px border border-white/10" data-testid="bulk-results">
                {bulkResults.map((r, i) => (
                  <p key={i} className={`bg-navy px-4 py-2.5 text-xs font-mono ${r.error ? "text-red-400" : "text-emerald-400"}`}>
                    {r.error ? `✕ ${r.line} — ${r.error}` : `✓ ${r.lesson_title} — saved`}
                  </p>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <AssetLibraryPanel />

      <SectionVideoPanel />

      <HelpVideoPanel modules={modules} />

      <StuckInsightsPanel />

      <div className="space-y-6">
        {modules.map((m) => (
          <div key={m.id} className="border border-white/10" data-testid={`video-module-${m.id}`}>
            <div className="bg-steel px-5 py-3">
              <p className="font-mono text-[11px] tracking-[0.2em] text-safety">[{m.code}] <span className="text-white font-display font-bold tracking-normal text-sm ml-2">{m.title}</span></p>
            </div>
            <div className="divide-y divide-white/5 bg-charcoal">
              {m.lessons.map((l) => {
                const key = `${m.id}:${l.id}`;
                const d = draftFor(key);
                const rec = records[key];
                return (
                  <div key={key} className="px-5 py-4" data-testid={`video-row-${key.replace(":", "-")}`}>
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <p className="text-sm text-slate-200 font-medium">{l.title}</p>
                      {rec && (
                        <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border ${
                          rec.status === "published" ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" : "text-amber-300 border-amber-400/40 bg-amber-400/10"
                        }`} data-testid={`video-status-${key.replace(":", "-")}`}>{rec.status} · {rec.provider}</span>
                      )}
                    </div>
                    <div className="grid sm:grid-cols-12 gap-2.5">
                      <input type="url" placeholder="https://youtube.com/watch?v=…" value={d.video_url}
                        onChange={(e) => setDraft(key, { video_url: e.target.value })}
                        data-testid={`video-input-${key.replace(":", "-")}`} className={`${inputCls} sm:col-span-5`} />
                      <input placeholder="Duration (12:30)" value={d.duration}
                        onChange={(e) => setDraft(key, { duration: e.target.value })}
                        data-testid={`video-duration-${key.replace(":", "-")}`} className={`${inputCls} sm:col-span-2`} />
                      <input placeholder="Description (optional)" value={d.description}
                        onChange={(e) => setDraft(key, { description: e.target.value })}
                        data-testid={`video-desc-${key.replace(":", "-")}`} className={`${inputCls} sm:col-span-3`} />
                      <select value={d.status} onChange={(e) => setDraft(key, { status: e.target.value })}
                        data-testid={`video-status-select-${key.replace(":", "-")}`} className={`${inputCls} sm:col-span-2`}>
                        <option value="published">Published</option>
                        <option value="draft">Draft</option>
                      </select>
                    </div>
                    <div className="flex gap-2 mt-2.5">
                      <button onClick={() => save(key)} data-testid={`video-save-${key.replace(":", "-")}`}
                        className={`flex items-center gap-2 text-xs font-semibold px-4 py-2 transition-colors ${
                          savedKey === key ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/40" : "bg-safety hover:bg-safety-hover text-white"
                        }`}>
                        {savedKey === key ? <CheckCircle2 size={13} /> : <Save size={13} />}
                        {savedKey === key ? "Saved" : "Save"}
                      </button>
                      {rec && (
                        <button onClick={() => remove(key)} data-testid={`video-delete-${key.replace(":", "-")}`}
                          className="flex items-center gap-2 text-xs font-semibold px-4 py-2 border border-white/15 text-slate-400 hover:text-red-400 hover:border-red-500/40 transition-colors">
                          <Trash2 size={13} /> Delete
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
